import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import inventoryApi from '../../../services/inventoryApi';
import '../inventory.css';
import './ProductForm.css';

const ProductPriceLists = ({ organization = 'TestOrg' }) => {
  const { t } = useTranslation();
  const [products, setProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [priceLists, setPriceLists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [productSearchTerm, setProductSearchTerm] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [tableColumns, setTableColumns] = useState([]);
  const [columnSettings, setColumnSettings] = useState({});
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [showColumnMenu, setShowColumnMenu] = useState(null);
  const [sidebarWidth, setSidebarWidth] = useState(280);
  const [isResizing, setIsResizing] = useState(false);
  const [editFormData, setEditFormData] = useState({
    price_list_name: '',
    currency: 'USD',
    unit_price: '',
    min_quantity: 1,
    max_quantity: null,
    start_date: '',
    end_date: '',
    is_active: true
  });

  useEffect(() => {
    if (organization) {
      const loadData = async () => {
        loadColumnSettings();
        await loadProducts();
      };
      loadData();
    }
  }, [organization]);

  // Handle resize
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isResizing) return;
      const newWidth = e.clientX;
      if (newWidth >= 200 && newWidth <= 600) {
        setSidebarWidth(newWidth);
      }
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isResizing]);

  useEffect(() => {
    if (selectedProduct) {
      loadPriceLists(selectedProduct);
    }
  }, [selectedProduct]);

  // Load column settings from localStorage
  const loadColumnSettings = () => {
    const saved = localStorage.getItem(`priceListsColumns_${organization}`);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        setColumnSettings(parsed.settings || {});
        setSortConfig(parsed.sort || { key: null, direction: 'asc' });
        return parsed.settings || {};
      } catch (e) {
        console.error('Error loading column settings:', e);
        return {};
      }
    }
    return {};
  };

  // Save column settings to localStorage
  const saveColumnSettings = (settings, sort) => {
    localStorage.setItem(`priceListsColumns_${organization}`, JSON.stringify({
      settings,
      sort
    }));
  };

  const loadProducts = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await inventoryApi.getProducts(organization);
      const productsData = Array.isArray(response) ? response : (response.data || []);
      setProducts(productsData);
      if (productsData.length > 0) {
        setSelectedProduct(productsData[0].id);
      }
    } catch (err) {
      setError(err.message || 'Failed to load products');
    } finally {
      setLoading(false);
    }
  };

  const loadPriceLists = async (productId) => {
    try {
      setLoading(true);
      setError(null);
      const response = await inventoryApi.getProductPriceLists(productId);
      const priceListsData = Array.isArray(response) ? response : (response.data || []);
      setPriceLists(priceListsData);

      // Generate dynamic columns based on the first price list
      if (priceListsData.length > 0) {
        const saved = localStorage.getItem(`priceListsColumns_${organization}`);
        let savedSettings = {};
        if (saved) {
          try {
            const parsed = JSON.parse(saved);
            savedSettings = parsed.settings || {};
          } catch (e) {
            console.error('Error parsing saved settings:', e);
          }
        }

        const firstItem = priceListsData[0];
        const columns = Object.keys(firstItem)
          .filter(key => key !== 'id' && key !== 'product_id' && key !== 'productId')
          .map((key, index) => ({
            key,
            label: formatColumnLabel(key),
            visible: savedSettings[key]?.visible !== false,
            order: savedSettings[key]?.order ?? index
          }));
        columns.sort((a, b) => a.order - b.order);
        setTableColumns(columns);
      }
    } catch (err) {
      setError(err.message || 'Failed to load price lists');
      setPriceLists([]);
    } finally {
      setLoading(false);
    }
  };

  // Helper function to format column labels
  const formatColumnLabel = (key) => {
    return key
      .replace(/_/g, ' ')
      .replace(/\b\w/g, char => char.toUpperCase());
  };

  // Toggle column visibility
  const toggleColumnVisibility = (key) => {
    const newSettings = {
      ...columnSettings,
      [key]: {
        ...columnSettings[key],
        visible: !(columnSettings[key]?.visible ?? true)
      }
    };
    setColumnSettings(newSettings);
    saveColumnSettings(newSettings, sortConfig);

    setTableColumns(prev => prev.map(col =>
      col.key === key ? { ...col, visible: !col.visible } : col
    ));
  };

  // Move column up in order
  const moveColumnUp = (key) => {
    const currentIndex = tableColumns.findIndex(col => col.key === key);
    if (currentIndex <= 0) return;

    const newColumns = [...tableColumns];
    [newColumns[currentIndex - 1], newColumns[currentIndex]] =
      [newColumns[currentIndex], newColumns[currentIndex - 1]];

    const newSettings = { ...columnSettings };
    newColumns.forEach((col, index) => {
      newSettings[col.key] = { ...newSettings[col.key], order: index };
    });

    setColumnSettings(newSettings);
    saveColumnSettings(newSettings, sortConfig);
    setTableColumns(newColumns);
  };

  // Move column down in order
  const moveColumnDown = (key) => {
    const currentIndex = tableColumns.findIndex(col => col.key === key);
    if (currentIndex >= tableColumns.length - 1) return;

    const newColumns = [...tableColumns];
    [newColumns[currentIndex], newColumns[currentIndex + 1]] =
      [newColumns[currentIndex + 1], newColumns[currentIndex]];

    const newSettings = { ...columnSettings };
    newColumns.forEach((col, index) => {
      newSettings[col.key] = { ...newSettings[col.key], order: index };
    });

    setColumnSettings(newSettings);
    saveColumnSettings(newSettings, sortConfig);
    setTableColumns(newColumns);
  };

  // Handle sorting
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    const newSortConfig = { key, direction };
    setSortConfig(newSortConfig);
    saveColumnSettings(columnSettings, newSortConfig);
  };

  // Sort price lists
  const sortedPriceLists = React.useMemo(() => {
    let sortable = [...priceLists];
    if (sortConfig.key) {
      sortable.sort((a, b) => {
        const aVal = a[sortConfig.key];
        const bVal = b[sortConfig.key];

        if (aVal === null || aVal === undefined) return 1;
        if (bVal === null || bVal === undefined) return -1;

        if (aVal < bVal) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (aVal > bVal) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }
    return sortable;
  }, [priceLists, sortConfig]);

  // Filter price lists
  const filteredPriceLists = sortedPriceLists.filter(pl => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return Object.values(pl).some(value =>
      value?.toString().toLowerCase().includes(term)
    );
  });

  // Get visible columns
  const visibleColumns = tableColumns.filter(col => col.visible);

  // Reset column settings
  const resetColumnSettings = () => {
    localStorage.removeItem(`priceListsColumns_${organization}`);
    setColumnSettings({});
    setSortConfig({ key: null, direction: 'asc' });
    if (selectedProduct) {
      loadPriceLists(selectedProduct);
    }
  };

  // Helper function to format cell values
  const formatCellValue = (value, key) => {
    if (value === null || value === undefined) return '-';

    // Format boolean values
    if (typeof value === 'boolean') {
      return value ? 'Yes' : 'No';
    }

    // Format price/cost fields
    if ((key.includes('price') || key.includes('cost')) && typeof value === 'number') {
      return `$${value.toFixed(2)}`;
    }

    // Format dates
    if (key.includes('date') || key.includes('created') || key.includes('updated')) {
      try {
        const date = new Date(value);
        if (!isNaN(date.getTime())) {
          return date.toLocaleDateString();
        }
      } catch (e) {
        return value;
      }
    }

    return value.toString();
  };

  const handleOpenModal = (priceList = null) => {
    if (priceList) {
      setEditingId(priceList.id);
      setEditFormData({
        price_list_name: priceList.price_list_name || priceList.priceListName || '',
        currency: priceList.currency || 'USD',
        unit_price: priceList.unit_price || priceList.unitPrice || '',
        min_quantity: priceList.min_quantity || priceList.minQuantity || 1,
        max_quantity: priceList.max_quantity || priceList.maxQuantity || null,
        start_date: priceList.start_date || priceList.startDate || '',
        end_date: priceList.end_date || priceList.endDate || '',
        is_active: priceList.is_active !== undefined ? priceList.is_active : (priceList.isActive !== undefined ? priceList.isActive : true)
      });
    } else {
      setEditingId(null);
      setEditFormData({
        price_list_name: '',
        currency: 'USD',
        unit_price: '',
        min_quantity: 1,
        max_quantity: null,
        start_date: '',
        end_date: '',
        is_active: true
      });
    }
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingId(null);
  };

  const handleSave = async () => {
    if (!selectedProduct) {
      setError('Please select a product first');
      return;
    }

    try {
      setError(null);
      const data = {
        ...editFormData,
        unit_price: parseFloat(editFormData.unit_price) || 0,
        min_quantity: parseInt(editFormData.min_quantity) || 1,
        max_quantity: editFormData.max_quantity ? parseInt(editFormData.max_quantity) : null
      };

      if (editingId) {
        await inventoryApi.updateProductPriceList(selectedProduct, editingId, data);
      } else {
        await inventoryApi.createProductPriceList(selectedProduct, data);
      }
      await loadPriceLists(selectedProduct);
      handleCloseModal();
    } catch (err) {
      setError(err.message || 'Failed to save price list');
    }
  };

  const handleDelete = async (priceListId) => {
    if (!window.confirm('Are you sure you want to delete this price list?')) {
      return;
    }

    try {
      setError(null);
      await inventoryApi.deleteProductPriceList(selectedProduct, priceListId);
      await loadPriceLists(selectedProduct);
    } catch (err) {
      setError(err.message || 'Failed to delete price list');
    }
  };

  if (loading && products.length === 0) {
    return (
      <div className="pm-page-card pm-skeleton" aria-busy="true">
        {t('inventory.loading_products')}
      </div>
    );
  }

  if (error && products.length === 0) {
    return (
      <div className="pm-page-card">
        <div className="pm-error-banner">
          <span aria-hidden="true">⚠️</span>
          <span>{error}</span>
        </div>
        <button className="pm-btn-primary" onClick={loadProducts}>
          {t('inventory.retry_button')}
        </button>
      </div>
    );
  }

  // Filter products for the side list
  const filteredProducts = products.filter(product => {
    if (!productSearchTerm) return true;
    const term = productSearchTerm.toLowerCase();
    return (
      product.name?.toLowerCase().includes(term) ||
      product.sku?.toLowerCase().includes(term) ||
      product.SKU?.toLowerCase().includes(term)
    );
  });

  const selectedProductData = products.find(p => p.id === selectedProduct);

  return (
    <div className="pm-page-card pm-split-layout" style={{ '--sidebar-width': `${sidebarWidth}px` }}>
      {/* Product Selection Sidebar */}
      <div className="pm-sidebar">
        <h3>{t('inventory.products_heading')}</h3>
        <div className="pm-sidebar-search">
          <input
            type="search"
            placeholder={t('inventory.search_products_placeholder')}
            value={productSearchTerm}
            onChange={(e) => setProductSearchTerm(e.target.value)}
          />
        </div>
        <div className="pm-product-list-wrap">
          {filteredProducts.length === 0 ? (
            <div className="pm-empty-state compact">
              <p>{t('inventory.no_products_found')}</p>
            </div>
          ) : (
            <ul className="pm-product-list">
              {filteredProducts.map(product => (
                <li 
                  key={product.id}
                  onClick={() => setSelectedProduct(product.id)}
                  className={`pm-product-list-item ${selectedProduct === product.id ? 'selected' : ''}`}
                >
                  <div className="pm-product-list-name">{product.name}</div>
                  <div className="pm-product-list-sku">
                    {t('inventory.sku_prefix')} {product.sku || product.SKU || 'N/A'}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* Resizer */}
      <div
        onMouseDown={() => setIsResizing(true)}
        className={`pm-resizer ${isResizing ? 'resizing' : ''}`}
      >
        <div className="pm-resizer-icon">⋮</div>
      </div>

      {/* Main Content Area */}
      <div className="pm-main-panel">
        <div className="pm-header">
          <h3 className="pm-header-title">
            {t('inventory.price_lists_title')}
            {selectedProductData && (
              <span className="pm-context-title">
                {t('inventory.product_variants_for')} {selectedProductData.name}
              </span>
            )}
          </h3>
          <button className="pm-btn-primary" onClick={() => handleOpenModal()} disabled={!selectedProduct}>
            <span aria-hidden="true">➕</span>
            <span>{t('inventory.add_price_list_button')}</span>
          </button>
        </div>

        {error && (
          <div className="pm-error-banner">
            <span aria-hidden="true">⚠️</span>
            <span>{error}</span>
          </div>
        )}

        <div className="pm-toolbar">
          <div className="pm-search-wrap">
            <input
              type="search"
              placeholder={t('inventory.search_price_lists_placeholder')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        {!selectedProduct ? (
          <div className="pm-empty-state">
            <div className="pm-empty-state-icon" aria-hidden="true">📦</div>
            <p>{t('inventory.select_product_price_lists_message')}</p>
          </div>
        ) : priceLists.length === 0 && !loading ? (
          <div className="pm-empty-state">
            <div className="pm-empty-state-icon" aria-hidden="true">💰</div>
            <h3>{t('inventory.no_price_lists_found')}</h3>
            <button className="pm-btn-primary" onClick={() => handleOpenModal()}>
              <span aria-hidden="true">➕</span>
              {t('inventory.add_price_list_button')}
            </button>
          </div>
        ) : (
          <div className="pm-table-wrap">
            <table className="pm-table" role="grid">
              <thead>
                <tr>
                  <th style={{ width: '100px' }}>
                    <div className="pm-header-content">
                      {tableColumns.filter(c => !c.visible).length > 0 && (
                        <button
                          className="pm-column-btn visibility-btn"
                          onClick={() => setShowColumnMenu(showColumnMenu === 'hidden' ? null : 'hidden')}
                          title={t('inventory.show_hidden_columns')}
                        >
                          ⊕
                        </button>
                      )}
                      <div className="pm-header-label">{t('inventory.actions_header')}</div>
                    </div>
                    {showColumnMenu === 'hidden' && (
                      <div className="pm-column-menu">
                        <div className="menu-header">{t('inventory.hidden_columns_header')}</div>
                        {tableColumns.filter(c => !c.visible).map(col => (
                          <button key={col.key} onClick={() => {
                            toggleColumnVisibility(col.key);
                          }}>
                            {t('inventory.show_column')} {col.label}
                          </button>
                        ))}
                        <hr />
                        <button onClick={() => {
                          resetColumnSettings();
                          setShowColumnMenu(null);
                        }}>
                          {t('inventory.reset_columns')}
                        </button>
                      </div>
                    )}
                  </th>
                  {visibleColumns.map((col, index) => (
                    <th key={col.key} className="pm-sortable-header">
                      <div className="pm-header-content">
                        <div className="pm-header-controls">
                          <button
                            className="pm-column-btn"
                            onClick={() => moveColumnUp(col.key)}
                            disabled={index === 0}
                            title={t('inventory.move_left_tooltip')}
                          >
                            ◀
                          </button>
                          <button
                            className="pm-column-btn"
                            onClick={() => moveColumnDown(col.key)}
                            disabled={index === visibleColumns.length - 1}
                            title={t('inventory.move_right_tooltip')}
                          >
                            ▶
                          </button>
                          <button
                            className="pm-column-btn visibility-btn"
                            onClick={() => setShowColumnMenu(showColumnMenu === col.key ? null : col.key)}
                            title={t('inventory.column_options_tooltip')}
                          >
                            ⋮
                          </button>
                        </div>
                        <div className="pm-header-label" onClick={() => handleSort(col.key)}>
                          {col.label}
                          {sortConfig.key === col.key && (
                            <span className="pm-sort-indicator">
                              {sortConfig.direction === 'asc' ? ' ▲' : ' ▼'}
                            </span>
                          )}
                        </div>
                      </div>
                      {showColumnMenu === col.key && (
                        <div className="pm-column-menu">
                          <button onClick={() => {
                            toggleColumnVisibility(col.key);
                            setShowColumnMenu(null);
                          }}>
                            {t('inventory.hide_column')}
                          </button>
                        </div>
                      )}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={visibleColumns.length + 1}>
                      <div className="pm-skeleton" aria-busy="true">{t('inventory.loading_price_lists')}</div>
                    </td>
                  </tr>
                ) : (
                  filteredPriceLists.map((pl) => (
                    <tr key={pl.id}>
                      <td>
                        <div className="pm-row-actions">
                          <button className="pm-row-btn edit" onClick={() => handleOpenModal(pl)} title={t('inventory.edit_tooltip')}>
                            <span aria-hidden="true">✏️</span>
                          </button>
                          <button className="pm-row-btn delete" onClick={() => handleDelete(pl.id)} title={t('inventory.delete_tooltip')}>
                            <span aria-hidden="true">🗑️</span>
                          </button>
                        </div>
                      </td>
                      {visibleColumns.map(col => (
                        <td key={col.key}>
                          {formatCellValue(pl[col.key], col.key)}
                        </td>
                      ))}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {isModalOpen && (
        <div className="pm-modal-overlay">
          <div className="pm-modal-content">
            <div className="pm-modal-header">
              <h2>{editingId ? t('inventory.edit_price_list_title') : t('inventory.add_price_list_title')}</h2>
              <button className="pm-modal-close" onClick={handleCloseModal}>×</button>
            </div>
            <div className="pm-modal-body">
              <div className="pm-form-grid">
                <div className="pm-form-group">
                  <label htmlFor="pl-name">{t('inventory.price_list_name_label')}</label>
                  <input
                    id="pl-name"
                    type="text"
                    value={editFormData.price_list_name}
                    onChange={(e) => setEditFormData({ ...editFormData, price_list_name: e.target.value })}
                  />
                </div>

                <div className="pm-form-group">
                  <label htmlFor="pl-currency">{t('inventory.currency_label')}</label>
                  <select
                    id="pl-currency"
                    value={editFormData.currency}
                    onChange={(e) => setEditFormData({ ...editFormData, currency: e.target.value })}
                  >
                    <option value="USD">USD</option>
                    <option value="EUR">EUR</option>
                    <option value="GBP">GBP</option>
                    <option value="CAD">CAD</option>
                  </select>
                </div>

                <div className="pm-form-group">
                  <label htmlFor="pl-price">{t('inventory.unit_price_label')}</label>
                  <input
                    id="pl-price"
                    type="number"
                    step="0.01"
                    value={editFormData.unit_price}
                    onChange={(e) => setEditFormData({ ...editFormData, unit_price: e.target.value })}
                  />
                </div>

                <div className="pm-form-group">
                  <label htmlFor="pl-min">{t('inventory.min_quantity_label')}</label>
                  <input
                    id="pl-min"
                    type="number"
                    value={editFormData.min_quantity}
                    onChange={(e) => setEditFormData({ ...editFormData, min_quantity: e.target.value })}
                  />
                </div>

                <div className="pm-form-group">
                  <label htmlFor="pl-max">{t('inventory.max_quantity_label')}</label>
                  <input
                    id="pl-max"
                    type="number"
                    value={editFormData.max_quantity || ''}
                    onChange={(e) => setEditFormData({ ...editFormData, max_quantity: e.target.value })}
                  />
                </div>

                <div className="pm-form-group">
                  <label htmlFor="pl-start">{t('inventory.start_date_label')}</label>
                  <input
                    id="pl-start"
                    type="date"
                    value={editFormData.start_date}
                    onChange={(e) => setEditFormData({ ...editFormData, start_date: e.target.value })}
                  />
                </div>

                <div className="pm-form-group">
                  <label htmlFor="pl-end">{t('inventory.end_date_label')}</label>
                  <input
                    id="pl-end"
                    type="date"
                    value={editFormData.end_date}
                    onChange={(e) => setEditFormData({ ...editFormData, end_date: e.target.value })}
                  />
                </div>

                <div className="pm-toggle-row">
                  <label htmlFor="pl-active">
                    <input
                      id="pl-active"
                      type="checkbox"
                      role="switch"
                      checked={editFormData.is_active}
                      onChange={(e) => setEditFormData({ ...editFormData, is_active: e.target.checked })}
                    />
                    {t('inventory.active_label')}
                  </label>
                </div>
              </div>
            </div>
            <div className="pm-modal-footer">
              <button className="pm-btn-secondary" onClick={handleCloseModal} type="button">
                {t('inventory.cancel_button')}
              </button>
              <button className="pm-btn-primary" onClick={handleSave} type="button">
                {editingId ? t('inventory.update_category_button') : t('inventory.create_category_button')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductPriceLists;
